import React, { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import Slider, { SliderMode, SliderRef } from '../Components/Slider/Slider';
import SliderArrow from './Assets/icons/SliderArrow';
import './KodanshaSlider.scss';

interface KodanshaSliderProps {
    title: ReactNode;
    subtitle?: ReactNode;
    thumbnailSelector?: string;
    children: ReactElement[];
}

export default function KodanshaSlider(
    { title, subtitle, thumbnailSelector = '[class^=Item-Thumbnail-]', children }: KodanshaSliderProps): ReactElement {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const sliderRef = useRef<SliderRef | null>(null);

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [navBtnSize, setNavBtnSize] = useState({ width: 0, height: 0 });
    const [maxPage, setMaxPage] = useState(0);
    const [page, setPage] = useState(0);

    useEffect(() => {
        const onResize = (): void => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', onResize);
        return (): void => {
            window.removeEventListener('resize', onResize);
        };
    }, []);

    useEffect(() => {
        const { current: container } = containerRef;
        if (container === null) {
            return;
        }
        const observer = new ResizeObserver(() => {
            let maxWidth = 0;
            let maxHeight = 0;
            for (const el of Array.from(container.querySelectorAll(thumbnailSelector))) {
                const rect = el.getBoundingClientRect();
                maxWidth = Math.max(maxWidth, rect.width);
                maxHeight = Math.max(maxHeight, rect.height);
            }
            setNavBtnSize({
                width: Math.min(maxWidth, (document.body.clientWidth - container.offsetWidth - 30) / 2),
                height: maxHeight,
            });
        });
        observer.observe(container);
        return (): void => {
            observer.unobserve(container);
        };
    }, [thumbnailSelector]);

    return (
        <section className='KodanshaSlider'>
            <div className='KodanshaSlider-contentWrapper'>
                {subtitle && <h3>{subtitle}</h3>}
                <h2>{title}</h2>
                <div className='KodanshaSlider-sliderWrapper' ref={containerRef}>
                    <Slider
                        ref={sliderRef}
                        mode={windowWidth < 768 ? SliderMode.Free : SliderMode.Fixed}
                        onMaxPageChange={setMaxPage}
                        onPageChange={setPage}
                    >
                        {children}
                    </Slider>
                    {maxPage > 0 && (
                        <div className='KodanshaSlider-bullets'>
                            {((): ReactElement[] => {
                                const bullets = [];
                                for (let i = 0; i <= maxPage; i++) {
                                    bullets.push(
                                        <button
                                            key={i}
                                            type='button'
                                            className={i === page ? 'active' : ''}
                                            title={`Page ${i + 1}`}
                                            onClick={(): void => {
                                                sliderRef.current?.goToPage(i);
                                            }}
                                        />,
                                    );
                                }
                                return bullets;
                            })()}
                        </div>
                    )}
                    <div className={'KodanshaSlider-btnWrapper prev' + (page > 0 ? ' visible' : '')}>
                        <button type='button' style={navBtnSize} tabIndex={-1} onClick={(): void => {
                            sliderRef.current?.goToPrevPage();
                        }}>
                            <SliderArrow />
                        </button>
                    </div>
                    <div className={'KodanshaSlider-btnWrapper next' + (page < maxPage ? ' visible' : '')}>
                        <button type='button' style={navBtnSize} tabIndex={-1} onClick={(): void => {
                            sliderRef.current?.goToNextPage();
                        }}>
                            <SliderArrow />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
}
